import { Box, Button, Divider, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContactType } from '@store/common/organisation.enums';
import { ManageContact } from '@store/common/organisation.model';

import { checkIfErrors, FieldValidationType, getFieldError } from '@utils/yup.utils';

import SelectComponent from '@components/inputs/select/Select.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';

import AsyncSelectWithSearchComponent from '@components/inputs/asyncSelect/AsyncSelectWithSearch.component';
import { Sizes } from '@constants/sizes.constants';
import { Close } from '@icons/index';
import { usersService } from '@store/settings/users';
import { map } from 'rxjs';

interface ManageContactDrawerProps {
  contact?: ManageContact;
  handleAdd: (contact: ManageContact) => void;
  handleClose: () => void;
  readOnly?: boolean;
}

const ManageContactDrawer = (props: ManageContactDrawerProps) => {
  const { contact: contactProps, handleAdd, handleClose, readOnly } = props;

  const { t } = useTranslation();

  const [contact, setContact] = useState<ManageContact>(contactProps || {});

  const updateContact = (name: string) => (value: any) => setContact((state) => ({ ...state, [name]: value }));
  const updateContactAddress = (name: string) => (value: any) => setContact((state) => ({
    ...state,
    address: { ...state.address, [name]: value }
  }));

  const errors = (!readOnly) ? {
    title: getFieldError(contact.title, FieldValidationType.REQUIRED_STRING),
    contactRole: getFieldError(contact.contactRole, FieldValidationType.REQUIRED_STRING),
    name: getFieldError(contact.name, FieldValidationType.REQUIRED_STRING),
    surName: getFieldError(contact.surName, FieldValidationType.REQUIRED_STRING),
    email: getFieldError(contact.email, FieldValidationType.REQUIRED_EMAIL),
  } : {};

  return (
    <Drawer anchor="right" open onClose={handleClose}>
      <Stack flex={1} overflow="hidden" pr="10px">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb="12px" mr="-8px">
          <Typography fontSize={18} fontWeight="700">
            {t('unitDetails.contacts.drawerTitle')}
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Stack>
        <Divider variant="fullWidth" orientation="horizontal" />
        <Stack py="20px" spacing={2}>
          <SelectComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.type')}
            placeholder={t('global.writeSomething')}
            handleChange={updateContact('contactRole')}
            value={contact.contactRole}
            error={errors.contactRole}
            items={ContactType.selectItems()}
            readOnly={readOnly}
            required />
          {
            [ContactType.INTERNAL_OPERATIONAL, ContactType.INTERNAL_COMMERCIAL, ContactType.INTERNAL_ADMINISTRATIVE].includes(contact?.contactRole || ContactType.UNDEFINED) ? (
              <AsyncSelectWithSearchComponent
                labelWidth={Sizes.drawerInputLabelWidth}
                label={t('invoices.invoicesValidation.table.user')}
                placeholder={t('global.selectOption')}
                handleChange={(item: any) => {
                  updateContact('name')(item.firstName);
                  updateContact('surName')(item.lastName);
                  updateContact('email')(item.email);
                }}
                value={contact.surName ? { label: `${contact.surName} ${contact.name}`, value: contact.id } : undefined}
                getOptions={(search) => {
                  usersService.setFilters('search')(search);
                  return usersService.getUsers().pipe(
                    map((options) => options.data.map((o) => ({ label: `${o.firstName} ${o.lastName}`, firstName: o.firstName, lastName: o.lastName, email: o.email, value: o.id })))
                  );
                }
                }
              //error={errors.organisation}
              />
            ) : ''
          }
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.name')}
            placeholder={t('global.writeSomething')}
            handleChange={updateContact('title')}
            value={contact.title || ''}
            error={errors.title}
            readOnly={readOnly}
            required />
          <SelectComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.type')}
            placeholder={t('global.writeSomething')}
            handleChange={updateContact('contactRole')}
            value={contact.contactRole}
            error={errors.contactRole}
            items={ContactType.selectItems()}
            readOnly={readOnly}
            required />
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.lastname')}
            placeholder={t('global.writeSomething')}
            handleChange={updateContact('surName')}
            value={contact.surName || ''}
            error={errors.surName}
            readOnly={readOnly}
            required />
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.firstname')}
            placeholder={t('global.writeSomething')}
            handleChange={updateContact('name')}
            value={contact.name || ''}
            error={errors.name}
            readOnly={readOnly}
            required />
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.phone')}
            placeholder={t('global.writeSomething')}
            handleChange={updateContact('phone')}
            value={contact.phone}
            readOnly={readOnly} />
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.email')}
            placeholder={t('global.writeSomething')}
            handleChange={updateContact('email')}
            value={contact.email || ''}
            error={errors.email}
            readOnly={readOnly}
            required />
          <TextFieldComponent
            multiline
            minRows={1}
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.comment')}
            placeholder={t('global.writeSomething')}
            handleChange={updateContact('comment')}
            value={contact.comment}
            readOnly={readOnly} />
        </Stack>
        <Divider variant="fullWidth" orientation="horizontal" />
        <Stack py="20px" spacing={2}>
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.street1')}
            placeholder={t('global.writeSomething')}
            handleChange={updateContactAddress('street')}
            value={contact.address?.street || ''}
            // error={errors.address?.street}
            readOnly={readOnly}
          />
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.street2')}
            placeholder={t('global.writeSomething')}
            handleChange={updateContactAddress('street2')}
            value={contact.address?.street2 || ''}
            // error={errors.contactRole}
            readOnly={readOnly}
          />
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.street3')}
            placeholder={t('global.writeSomething')}
            handleChange={updateContactAddress('street3')}
            value={contact.address?.street3 || ''}
            readOnly={readOnly}
          />
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.city')}
            placeholder={t('global.writeSomething')}
            handleChange={updateContactAddress('city')}
            value={contact.address?.city || ''}
            readOnly={readOnly}
          />
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.zip')}
            placeholder={t('global.writeSomething')}
            handleChange={updateContactAddress('zip')}
            value={contact.address?.zip}
            readOnly={readOnly} />
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.country')}
            placeholder={t('global.writeSomething')}
            handleChange={updateContactAddress('country')}
            value={contact.address?.country || ''}
            readOnly={readOnly}
          />
        </Stack>
        <Divider variant="fullWidth" orientation="horizontal" />
        {!readOnly && (
          <Box alignSelf="flex-end" justifySelf="flex-end" mt="40px" mb="20px">
            <Button
              variant="contained"
              color="primary"
              disabled={checkIfErrors(errors)}
              onClick={() => handleAdd(contact)}>
              {t(`global.${contactProps ? 'modify' : 'add'}`)}
            </Button>
          </Box>
        )}
      </Stack>
    </Drawer>
  );
};

export default ManageContactDrawer;
